// src/services/funFactService.ts

import { ELEMENT_DEFS } from "../components/elementDefinitions";
import type { ElementDef } from "../store/elementStore";

/**
 * Helper to title-case "steam" → "Steam"
 */
function titleCase(str: string) {
  return str
    .split(/[_-]+/) // split on underscore/hyphen
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Local fun-fact map for every element (Tier 1–7)
 */
export const LOCAL: Record<string, string> = {
  // Tier 1: base elements
  fire: "Fire has powered humanity for over a million years—cooking, forging, and lighting our nights.",
  water:
    "Water covers over 70% of Earth’s surface and is essential for every living cell.",
  earth:
    "Earth (soil) recycles nutrients, nourishing plants and sustaining ecosystems worldwide.",
  air: "Air is approximately 78% nitrogen and 21% oxygen—every breath you take fuels your body.",
  aether:
    'Aether was once imagined as the "fifth element" filling the heavens beyond our world.',
  metal:
    "Metal has underpinned human technology from Bronze Age tools to modern skyscrapers.",
  ice: "Ice can preserve life: glaciers lock up water for thousands of years and shape our planet.",
  light:
    "Light travels at 299,792 kilometers per second—and allows us to see the world around us.",
  dark: "Darkness is simply the absence of light—but it reveals the stars that light up our night sky.",
  life: "Life on Earth may have begun in the oceans as simple single-celled organisms billions of years ago.",

  // Tier 2 combos
  steam:
    "Steam is created when water meets intense heat—powering locomotives and turbines.",
  lava: "Lava flows from volcanoes, formed when molten rock (magma) reaches Earth’s surface.",
  mud: "Mud combines earth and water—used since ancient times to build sturdy bricks.",
  dust: "Dust drifts on air currents, carrying particles from deserts and offering clues to climate.",
  smoke:
    "Smoke rises when fire meets air—its color and smell tell stories of what’s burning.",
  rust: "Rust forms when metal reacts with water and air—an electrochemical transformation.",
  electricity:
    "Electricity in nature appears as lightning—an immense discharge of atmospheric energy.",
  clay: "Clay shapes when mud meets fire—used by humans to craft pottery for millennia.",
  glass:
    "Glass forms when sand and heat combine—resulting in a transparent, versatile material.",
  magma:
    "Magma beneath Earth’s crust contains molten rock and dissolved gases at extreme temperatures.",
  mist: "Mist hovers where water vapor cools in air—creating foggy mornings in valleys.",
  swamp:
    "Swamps form where water and life converge—rich ecosystems full of biodiversity.",
  ash: "Ash drifts from volcanic eruptions—its fine particles fertilize soils over time.",
  cloud:
    "Clouds form when water vapor condenses in air—driving weather patterns worldwide.",
  plasma:
    "Plasma is ionized gas—found in lightning, neon lights, and the surface of the Sun.",
  stone:
    "Stone, from earth and metal, has built landmarks like the pyramids and Stonehenge.",
  crystal:
    "Crystals grow in repeating patterns—forming gems prized for beauty and science.",
  snow: "Snowflakes form when water vapor freezes in air—each with a unique hexagonal pattern.",
  algae:
    "Algae produce half of Earth’s oxygen through photosynthesis in water bodies.",
  moss: "Moss carpets forest floors—thriving in damp, shaded habitats worldwide.",
  shadow:
    "Shadow is created when dark and air meet—offering relief from direct sunlight.",
  void: "Void represents empty space—where dark and aether intertwine beyond the stars.",
  energy:
    "Energy flows from light and aether—powering life, machines, and the cosmos.",
  spirit:
    "Spirit has been seen as the life force in many cultures—linking life and aether.",

  // Tier 3 combos
  geyser:
    "Geyser blasts water and steam when underground heat meets trapped water.",
  volcano:
    "Volcanoes erupt lava when magma and air pressure meet—reshaping landscapes.",
  storm:
    "Storms combine air and water—driving wind, rain, and weather extremes.",
  tsunami:
    "Tsunamis form when water and storm energy collide—creating massive ocean waves.",
  fog: "Fog drifts where mist and dust meet—reducing visibility and enchanting landscapes.",
  obsidian:
    "Obsidian forms when lava meets water—creating natural volcanic glass.",
  sand: "Sand grains combine stone and water—shaping beaches through endless waves.",
  brick:
    "Bricks are made by firing clay—used in construction since ancient civilizations.",
  pottery:
    "Pottery emerges when clay meets fire—preserving food and culture for thousands of years.",
  acid: "Acid can etch and dissolve materials—formed when water meets aether in certain reactions.",
  lightning:
    "Lightning strikes when electricity meets cloud moisture—discharging massive energy.",
  oasis: "Oases appear where water meets sand—providing life in arid deserts.",
  hurricane:
    "Hurricanes form from storm and ocean energy—powerful tropical cyclones.",
  magnet:
    "Magnets emerge when metal meets electricity—attracting iron and other metals.",
  steel:
    "Steel is forged when metal meets fire—stronger than iron and the backbone of industry.",
  ocean:
    "Oceans cover 71% of Earth—shaped by water meeting itself across vast distances.",
  mountain:
    "Mountains rise where earth meets earth—tectonic forces pushing land skyward.",
  desert:
    "Deserts expand where sand meets fire—harsh, arid regions with unique ecosystems.",
  forest:
    "Forests flourish where life meets earth—carbon sinks vital to global ecology.",
  meteor:
    "Meteors streak when fire meets stone—extraterrestrial rocks burning in our atmosphere.",
  gravity:
    "Gravity emerges where earth meets aether—pulling objects toward mass centers.",
  aurora:
    "Auroras glow when light meets charged air—painting skies near Earth’s poles.",
  time: "Time flows when aether meets void—an ever-ticking mystery of the universe.",

  // Tier 4 combos
  life_form:
    "Life Forms arise when life meets clay—imagining early living creatures.",
  golem:
    "Golems are mythical creatures of life and stone—brought to life by ancient magic.",
  phoenix:
    "Phoenixes blaze when life meets fire—reborn from their own ashes in legend.",
  dragon:
    "Dragons soar where phoenix meets lizard—mythical beasts of fire and earth.",
  plant:
    "Plants grow when life meets water—foundations of ecosystems and oxygen producers.",
  flower:
    "Flowers bloom where plant meets light—colorful stages of reproduction.",
  tree: "Trees rise where plant meets earth—ancient sentinels of forests worldwide.",
  fish: "Fish swim where life forms meet water—diverse species under the waves.",
  bird: "Birds fly where life forms meet air—evolved for flight with feathers and wings.",
  lizard:
    "Lizards crawl where life forms meet earth—cold-blooded reptiles of many environments.",
  human:
    "Humans emerged where life forms meet spirit—self-aware and tool-using species.",
  coral:
    "Coral reefs form where life meets ocean—diverse habitats teeming with marine life.",
  island:
    "Islands arise where mountain meets ocean—isolated ecosystems over water.",
  rainbow:
    "Rainbows appear where light meets rain—refractions painting sky arcs.",
  rain: "Rain falls where cloud meets water—life-giving drops from the sky.",
  diamond:
    "Diamonds form where crystal meets pressure—nature’s hardest known mineral.",
  pressure:
    "Pressure builds when gravity meets earth—deep inside planets and machines.",
  coal: "Coal forms where tree meets pressure—ancient plant matter turned to carbon.",
  oil: "Oil emerges where life meets pressure—fossil fuels powering modern industry.",
  electricity_arc:
    "Electricity Arcs flash where electricity meets plasma—bright, high-energy discharges.",

  // Tier 5 combos
  civilization:
    "Civilizations grow where human meets human—complex societies building cities and culture.",
  city: "Cities emerge where civilization meets earth—hubs of architecture and community.",
  tool: "Tools arise where human meets metal—extensions of our hands and minds.",
  farm: "Farms flourish where human meets plant—domestication feeding populations.",
  boat: "Boats sail where human meets water—bridging lands across seas.",
  wood: "Wood comes where tree meets tool—building homes, fires, and crafts.",
  paper: "Paper is made where wood meets tool—preserving knowledge on fibers.",
  wheel:
    "Wheels turn where tool meets wood—revolutionizing transport and machines.",
  cart: "Carts roll where wheel meets wood—haulage vehicles of pre-industrial eras.",
  cloth:
    "Cloth weaves where plant meets tool—fabrics clothing peoples through history.",
  medicine:
    "Medicine heals where plant meets knowledge—early remedies turned into pharmaceuticals.",
  knowledge:
    "Knowledge accumulates where human meets paper—libraries and digital archives.",
  music:
    "Music resonates where human meets metal—cymbals, strings, and wind instruments.",
  art: "Art expresses where human meets clay—pottery, sculpture, and painting.",
  metal_tool:
    "Metal Tools sharpen where tool meets metal—industrial advances and machinery.",
  armor: "Armor protects where metal meets cloth—worn by warriors across ages.",
  weapon:
    "Weapons impact where metal_tool meets fire—guns, swords, and explosives.",
  gunpowder:
    "Gunpowder blasts where fire meets coal—transformed warfare and mining.",
  explosion:
    "Explosions roar where fire meets gunpowder—massive energy released in an instant.",
  ship: "Ships voyage where boat meets wood—projecting power and trade across oceans.",
  war: "Wars erupt where civilization meets weapon—conflicts shaping human history.",
  peace:
    "Peace prevails when war meets knowledge—diplomacy and understanding flourish.",

  // Tier 6 combos
  machine:
    "Machines emerge where metal meets knowledge—automating tasks and industry.",
  electricity_generator:
    "Generators produce where machine meets magnet—power stations lighting cities.",
  battery:
    "Batteries store where electricity meets metal—portable energy for devices.",
  light_bulb:
    "Light Bulbs glow where electricity meets glass—illuminating homes and streets.",
  radio:
    "Radios broadcast where electricity meets metal—connecting voices worldwide.",
  telescope:
    "Telescopes explore where glass meets knowledge—revealing distant galaxies.",
  microscope:
    "Microscopes discover where glass meets knowledge—unveiling the microscopic world.",
  computer:
    "Computers compute where electricity meets logic—digital brains of modern life.",
  logic:
    "Logic flows where knowledge meets knowledge—foundation of reasoning and code.",
  internet:
    "Internet connects where computer meets computer—global network of information.",
  rocket:
    "Rockets launch where metal meets explosion—propelling us into space.",
  space_station:
    "Space Stations orbit where rocket meets habitat—astronauts living above Earth.",
  satellite:
    "Satellites circle where rocket meets radio—enabling communication and GPS.",
  robot:
    "Robots move where computer meets machine—performing tasks from factories to homes.",
  ai: "AI thinks where computer meets knowledge—simulating human intelligence.",
  clock: "Clocks tick where time meets machine—measuring moments precisely.",
  factory:
    "Factories hum where machine meets building—mass production of goods.",
  building:
    "Buildings rise where brick meets knowledge—architecture shaping skylines.",
  habitat:
    "Habitats sustain where building meets life—homes for humans and wildlife.",
  farm_machine:
    "Farm Machines harvest where machine meets farm—modernizing agriculture.",
  medicine_advanced:
    "Advanced Medicine heals where medicine meets microscope—targeted treatments.",

  // Tier 7 combos
  space_colony:
    "Space Colonies grow where space_station meets habitat—settling humanity beyond Earth.",
  terraforming:
    "Terraforming reshapes where space_colony meets earth—making new worlds livable.",
  virtual_reality:
    "Virtual Reality immerses where computer meets human—digital worlds at our fingertips.",
  cyborg:
    "Cyborgs merge where human meets robot—enhanced beings of flesh and machine.",
  bioengineering:
    "Bioengineering modifies where life meets microscope—designing living systems.",
  nanobot:
    "Nanobots operate where robot meets microscope—tiny machines in medicine and tech.",
  quantum_computer:
    "Quantum Computers compute where computer meets aether—harnessing quantum bits.",
  fusion_reactor:
    "Fusion Reactors burn where plasma meets pressure—clean energy of the future.",
  time_machine:
    "Time Machines warp where time meets machine—science fiction turned aspiration.",
  wormhole:
    "Wormholes bridge where void meets space—a shortcut through spacetime.",
  black_hole:
    "Black Holes form where gravity meets space—regions of extreme density.",
  space:
    "Space expands where void meets aether—final frontier of human exploration.",
  star: "Stars shine where plasma meets gravity—fusing elements in their cores.",
  planet: "Planets orbit where space meets earth—homes for life in the cosmos.",
  moon: "Moons reflect where planet meets light—Earth’s companion lighting our night.",
};

/**
 * Synchronous lookup of a fun fact for a given result.
 */
export function getLocalFunFact(
  result: string,
  _ingredients: string[]
): string | null {
  return LOCAL[result] || null;
}
